"use client";

import { useEffect, useState } from "react";
import {
  getImagesByPianificazione,
  updateImage,
  deleteImage,
  getImagesByBanner,
} from "../../../lib/api";
import "../../../theme/tailwind/table.css";
import { ExternalLink, Trash2 } from "lucide-react";
import { API_BASE } from "../../../lib/apiHelper";
import { devError } from "../../../utils/devConsole";

function normalizeUrl(url) {
  if (!url) return "";
  const trimmed = url.trim();
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) return trimmed;
  if (trimmed.startsWith("www.")) return "https://" + trimmed;
  if (/^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/.test(trimmed)) return "https://" + trimmed;
  return "";
}

export function Table({ numP, refreshKey, onDataUpdated, entityType }) {
  const [images, setImages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [urlErrors, setUrlErrors] = useState({});

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  useEffect(() => {
    fetchImages();
  }, [numP, refreshKey, onDataUpdated, entityType]);

  const fetchImages = async () => {
    if (isFirstLoad) setIsLoading(true);
    try {
      const res =
        entityType === "banner"
          ? await getImagesByBanner(numP)
          : await getImagesByPianificazione(numP);

      // Ordinamento delle immagini
      const sortedImages = res
        .map((img) => ({
          ...img,
          Ordine: Number(img.Ordine),
          NuovoTab: Number(img.NuovoTab),
        }))
        .sort((a, b) => {
          // Ordinamento principale per Ordine 1-6
          if (a.Ordine >= 1 && b.Ordine >= 1) return a.Ordine - b.Ordine;
          // I # (0) sempre per ultimi
          if (a.Ordine === 0) return 1;
          if (b.Ordine === 0) return -1;
          return 0;
        });

      setImages(sortedImages);
      setError(null);
    } catch (err) {
      setError("Errore durante il caricamento delle immagini.");
      devError("Error while loading images:", err);
    } finally {
      setIsLoading(false);
      setIsFirstLoad(false);
    }
  };

  const updateLocalImageState = (id, newFields) => {
    setImages((prev) =>
      prev.map((img) =>
        img.immagine_id === id ? { ...img, ...newFields } : img
      )
    );
  };

  const handleUrl = async (id, newValue, image) => {
    const trimmedValue = newValue.trim();
    try {
      updateLocalImageState(id, { Link: trimmedValue });
      await updateImage(id, { ...image, Link: trimmedValue });
      onDataUpdated();
    } catch (err) {
      setImages((prev) =>
        prev.map((img) =>
          img.immagine_id === id ? { ...img, Link: image.Link } : img
        )
      );
      devError("Error updating URL:", err);
    }
  };

  const handleOrder = async (id, newOrder, image) => {
    const numericOrder = Number(newOrder);
    try {
      updateLocalImageState(id, { Ordine: numericOrder });
      await updateImage(id, { ...image, Ordine: numericOrder });
      onDataUpdated();
    } catch (err) {
      setImages((prev) =>
        prev.map((img) =>
          img.immagine_id === id ? { ...img, Ordine: image.Ordine } : img
        )
      );
      devError("Error updating order:", err);
    }
  };

  const handleChangeToggle = async (id) => {
    const image = images.find((img) => img.immagine_id === id);
    const newTabValue = image.NuovoTab === 1 ? 0 : 1;
    try {
      updateLocalImageState(id, { NuovoTab: newTabValue });
      await updateImage(id, { ...image, NuovoTab: newTabValue });
      onDataUpdated();
    } catch (err) {
      devError("Error updating NuovoTab toggle:", err);
    }
  };

  const handleDeleteImage = async (id) => {
    try {
      await deleteImage(id);
      onDataUpdated();
    } catch (err) {
      devError("Error deleting image:", err);
    }
  };

  function isValidUrl(url) {
    // Deve contenere almeno un punto e un'estensione da 2 a 6 lettere
    return /\.[a-z]{2,6}(\/|$)/i.test(url);
  }

  // Rendering per mobile (vista a carte)
  if (isMobile) {
    return (
      <div className="w-full px-0">
        {isLoading && (
          <div className="flex justify-center py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#1F3E7C]"></div>
          </div>
        )}

        {error && <p className="text-red-600 py-2">{error}</p>}

        {images.length > 0 && (
          <div className="space-y-4" id={`TabellaImmagini${numP}`}>
            {images.map((image) => (
              <div
                key={image.immagine_id || `${image.File}-${image.Link}`}
                id={image.immagine_id}
                className="table-card"
              >
                <div className="flex items-center space-x-2">
                  <div className="w-24 h-16 flex-shrink-0">
                    <a
                      href={
                        image.File
                          ? `${API_BASE.replace(/\/$/, "")}/${image.File.replace(/^\/+/, "").replace(/\\/g, "/")}`
                          : "/default.jpg"
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img
                        className="w-24 h-16 object-contain rounded"
                        alt={image.Nome || "Immagine"}
                        src={
                          image.File
                            ? `${API_BASE.replace(/\/$/, "")}/${image.File.replace(/^\/+/, "").replace(/\\/g, "/")}?${new Date().getTime()}`
                            : "/default.jpg"
                        }
                      />
                    </a>
                  </div>

                  <div className="flex-1 flex flex-col space-y-2">
                    <div className="flex items-center justify-between w-full">
                      <button
                        onClick={() => handleChangeToggle(image.immagine_id)}
                        className={`${
                          image.NuovoTab === 1 ? "bg-[#1F3E7C]" : "bg-[#dc3545]"
                        } text-white rounded flex items-center justify-center flex-shrink-0 px-2 py-1 text-xs sm:px-3 sm:py-2 sm:text-sm min-w-[40px] sm:min-w-[48px]`}
                      >
                        {image.NuovoTab === 1 ? "si" : "no"}
                      </button>

                      {entityType !== "banner" && (
                        <select
                          value={image.Ordine}
                          onChange={(e) =>
                            handleOrder(
                              image.immagine_id,
                              Number(e.target.value),
                              image
                            )
                          }
                          className="border border-gray-300 rounded px-1 py-1 text-xs text-center"
                        >
                          <option className="text-center" value={0}>#</option>
                          {[1, 2, 3, 4, 5, 6].map((num) => (
                            <option className="text-center" key={num} value={num}>
                              {num}
                            </option>
                          ))}
                        </select>
                      )}

                      <button
                        className="bg-[#dc3545] text-white rounded flex items-center justify-center px-2 py-1 text-xs sm:px-3 sm:py-2 sm:text-sm min-w-[40px] sm:min-w-[48px] hover:bg-[#1F3E7C]"
                        title="EliminaImmagine"
                        type="button"
                        onClick={() => handleDeleteImage(image.immagine_id)}
                      >
                        <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                      </button>
                    </div>

                    <input
                      onBlur={(e) => {
                        const value = e.target.value.trim();
                        if (value && !isValidUrl(value)) {
                          setUrlErrors((prev) => ({ ...prev, [image.immagine_id]: "URL non valida. Inserisci un indirizzo completo (es: www.sito.com)" }));
                        } else {
                          setUrlErrors((prev) => ({ ...prev, [image.immagine_id]: null }));
                          handleUrl(image.immagine_id, value, image);
                        }
                      }}
                      id={`txtLink${numP}-${image.immagine_id}`}
                      className="table-input"
                      name="Nome"
                      type="text"
                      defaultValue={image.Link}
                      placeholder="URL"
                    />
                    {urlErrors[image.immagine_id] && (
                      <span className="text-red-600 text-xs">{urlErrors[image.immagine_id]}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!isLoading && images.length === 0 }
      </div>
    );
  }

  // Rendering per desktop (vista tabellare)
  return (
    <div className="w-full">
      {isLoading && (
        <div className="flex justify-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#1F3E7C]"></div>
        </div>
      )}

      {error && <p className="text-red-600 py-2">{error}</p>}

      {images.length > 0 && (
        <div className="overflow-visible">
          <table id={`TabellaImmagini${numP}`} className="table-container">
            <thead className="table-header">
              <tr>
                <th className="w-1/5 text-center">
                  <p className="font-bold mb-0 text-xs sm:text-sm">Immagine</p>
                </th>
                <th className="w-2/5 text-center">
                  <p className="font-bold mb-0 text-xs sm:text-sm">URL</p>
                </th>
                <th className="w-1/5 text-center">
                  <p className="font-bold mb-0">
                    <ExternalLink className="w-4 h-4 sm:w-5 sm:h-5 inline" />
                  </p>
                </th>
                <th className="w-1/5 text-center">
                  <p className="font-bold mb-0 text-xs sm:text-sm">#</p>
                </th>
                <th className="w-1/5 text-center">
                  <p className="font-bold mb-0">
                    <Trash2 className="w-4 h-4 sm:w-5 sm:h-5 inline" />
                  </p>
                </th>
              </tr>
            </thead>
            <tbody className="table-body">
              {images.map((image) => (
                <tr
                  key={image.immagine_id || `${image.File}-${image.Link}`}
                  id={image.immagine_id}
                >
                  <td className="table-image-container align-middle text-center">
                    <a
                      href={
                        image.File
                          ? `${API_BASE.replace(/\/$/, "")}/${image.File.replace(/^\/+/, "").replace(/\\/g, "/")}`
                          : "/default.jpg"
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block"
                    >
                      <img
                        className="mx-auto w-28 h-20 object-cover rounded bg-gray-200"
                        alt={image.Nome || "Immagine"}
                        src={
                          image.File
                            ? `${API_BASE.replace(/\/$/, "")}/${image.File.replace(/^\/+/, "").replace(/\\/g, "/")}?${new Date().getTime()}`
                            : "/default.jpg"
                        }
                      />
                    </a>
                  </td>
                  <td>
                    <div className="flex justify-center">
                      <input
                        onBlur={(e) => {
                          const value = e.target.value.trim();
                          if (value && !isValidUrl(value)) {
                            setUrlErrors((prev) => ({ ...prev, [image.immagine_id]: "URL non valida. Inserisci un indirizzo completo (es: www.sito.com)" }));
                          } else {
                            setUrlErrors((prev) => ({ ...prev, [image.immagine_id]: null }));
                            handleUrl(image.immagine_id, value, image);
                          }
                        }}
                        id={`txtLink${numP}-${image.immagine_id}`}
                        className="table-input"
                        name="Nome"
                        type="text"
                        defaultValue={image.Link}
                        placeholder="URL"
                      />
                    </div>
                    {urlErrors[image.immagine_id] && (
                      <span className="text-red-600 text-xs">{urlErrors[image.immagine_id]}</span>
                    )}
                  </td>
                  <td className="text-center">
                    <button
                      onClick={() => handleChangeToggle(image.immagine_id)}
                      className={
                        image.NuovoTab === 1
                          ? "btn-toggle-yes"
                          : "btn-toggle-no"
                      }
                    >
                      {image.NuovoTab === 1 ? "si" : "no"}
                    </button>
                  </td>
                  <td className="text-center">
                    <div className="flex justify-center">
                      <select
                        value={image.Ordine}
                        onChange={(e) =>
                          handleOrder(
                            image.immagine_id,
                            Number(e.target.value),
                            image
                          )
                        }
                        className="border border-gray-300 rounded px-1 py-1 text-xs text-center"
                      >
                        <option className="text-center" value={0}>#</option>
                        {[1, 2, 3, 4, 5, 6].map((num) => (
                          <option className="text-center" key={num} value={num}>
                            {num}
                          </option>
                        ))}
                      </select>
                    </div>
                  </td>
                  <td className="text-center">
                    <button
                      className="btn-delete mx-auto"
                      title="EliminaImmagine"
                      type="button"
                      onClick={() => handleDeleteImage(image.immagine_id)}
                    >
                      <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
